import type { VisitPopulated } from '@api/READ_ONLY/visits_api/Api';
import { LIST_DATE_FORMAT, LIST_TIME_FORMAT } from '@app/utils/dates/dateFormats';
import { useQuery } from '@tanstack/react-query';
import { Alert, Card, Empty, Segmented, Skeleton, Tag } from 'antd';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FieldHint } from '@app/components/ui/FieldHint';
import {
  DASHBOARD_PERIOD_LABEL_KEYS,
  DASHBOARD_PERIOD_ORDER,
  type DashboardPeriod,
  getDashboardPeriodWindow,
} from '@app/features/dashboard/dashboardPeriod';
import { listDoctorInstructions, listDoctorVisits, listMyPatients } from '@app/features/doctorPortal/api';
import DoctorVisitModal from '@app/features/doctorPortal/DoctorVisitModal';
import {
  buildMeasurementRows,
  type MetricAssessment,
  type MetricKey,
  type PatientMeasurements,
} from '@app/features/doctorPortal/measurementBaseline';
import type { DoctorPatient, DoctorInstruction } from '@app/features/doctorPortal/types';
import { inDisplayZone } from '@app/utils/dates/displayTimezone';

/**
 * The doctor's landing page.
 *
 * The doctor here is an external associate. Their job is to watch their patients'
 * MEASUREMENTS and to follow their own instructions — caregivers are the office's to
 * monitor, not theirs. So this page is one row of counts and three panels:
 *
 *   THE PERIOD    one Segmented — Today / This week / Last 30 days — over both
 *                 the tile row and the measurements panel. It is the admin
 *                 dashboard's control and window maths, imported not copied.
 *                 The tiles all count that same window, named once beside the
 *                 control rather than four times in four labels. The row this
 *                 replaced had a roster count, a week, an all-time ratio and an
 *                 unread tally side by side, which is why it read as four
 *                 unrelated numbers. Three of the four are counted off the
 *                 fetched rows, so they withhold themselves rather than
 *                 under-report if the window did not fit in one page.
 *                 Default is 30 days, not the admin's week: readings are sparse
 *                 and a week opens this page on an empty panel.
 *
 *   MEASUREMENTS  the point of the page. Every past visit that is not cancelled,
 *                 over 180 days, grouped per patient and LISTED over the
 *                 selected period — the 180 days are the baseline's, never the
 *                 reader's; each metric's newest value, annotated with the
 *                 patient's own median when the value has moved away from it.
 *                 The title says "latest" and an info hint says the rest,
 *                 because "Measurements" over a column of numbers left a reader
 *                 to guess between a reading, an average and a delta.
 *                 There is no endpoint for this and there does not need to be:
 *                 `/doctor/visits` returns VisitPopulated rows with the vitals
 *                 columns on them, so the whole thing is one query and some
 *                 arithmetic (measurementBaseline.ts).
 *   PROPOSALS     undecided ones, plus those decided recently. This page is the
 *                 only place a doctor ever learns a coordinator decided one —
 *                 nothing notifies them — but the decided ones age out, or the
 *                 panel becomes a log that never empties.
 *   COMING UP     the next few scheduled visits, by service. Not by caregiver:
 *                 who is going is the office's business.
 *
 * What was here before and is deliberately gone: a care-progress bar per patient,
 * which is completed-over-booked and therefore a measure of the OFFICE's
 * delivery, not of care; and an "awaiting your comment" queue, which framed the
 * doctor as owing a review of every caregiver's visit. Commenting is unchanged —
 * it just starts from a measurement now instead of from an obligation.
 *
 * NO MONEY anywhere, as everywhere else in this portal.
 */

// Baselines need history and the readings are sparse: over 30 days almost no
// patient reaches the three readings a median wants.
const BASELINE_DAYS = 180;

// One page covers every doctor: the busiest has ~270 past visits in 180 days and
// PaginationParams caps `limit` at 1000. If that ever stops being true the panel
// says so rather than averaging half a history — see `partial` below.
const MEASUREMENT_FETCH_LIMIT = 500;

const UPCOMING_MAX = 5;

// Every tile counts the SAME window. That is the whole point of the row: four
// numbers at four different scales is what made the old one unreadable. Three
// of the four are derived from these rows rather than from a `total`, so the
// fetch has to cover the widest period on offer — 30 days, in which prod's
// busiest doctor sees about 45 visits.
const PERIOD_FETCH_LIMIT = 500;

// 30 days, not the admin page's week. Readings are sparse enough that a week
// would open this page on an empty measurements panel.
const DEFAULT_DOCTOR_PERIOD: DashboardPeriod = '30d';

// How long a decided instruction stays on the page after the decision.
const INSTRUCTION_DECIDED_DAYS = 14;

const EMPTY = '—';

/**
 * Whether this visit carries any reading at all, and how many. Blood pressure
 * counts once, not twice: a doctor reading "4 new measurements" means four
 * things were measured, and systolic and diastolic are one of them.
 */
const countReadings = (visit: VisitPopulated): number => {
  const has = (value: unknown) => value !== null && value !== undefined && value !== '';
  return (
    Number(has(visit.bloodPressureSystolic) || has(visit.bloodPressureDiastolic)) +
    Number(has(visit.heartRate)) +
    Number(has(visit.oxygenSaturation)) +
    Number(has(visit.temperature)) +
    Number(has(visit.bloodGlucose)) +
    // Spirometry is one manoeuvre, so FVC and FEV1 count once between them.
    Number(has(visit.fvc) || has(visit.fev1))
  );
};

type Kpi = { key: string; labelKey: string; value: string };

const KpiTile = ({ kpi, label }: { kpi: Kpi; label: string }) => (
  <div data-testid={`doctor-kpi-${kpi.key}`} className="bg-white rounded-xl border border-slate-200 px-4 py-3.5">
    <div className="text-[12px] leading-tight text-slate-500 min-h-[30px]">{label}</div>
    <div className="mt-1.5 text-[26px] leading-none font-semibold text-slate-800 tabular-nums">{kpi.value}</div>
  </div>
);

/**
 * Label and unit per metric, reusing the keys that already exist. Systolic and
 * diastolic are assessed separately but read as one "168/95", so the pair has a
 * label of its own and the two components have none here.
 */
const METRIC_DISPLAY: Record<Exclude<MetricKey, 'diastolic'>, { labelKey: string; unit: string }> = {
  systolic: { labelKey: 'doctor-metric-blood-pressure', unit: 'mmHg' },
  heartRate: { labelKey: 'doctor-metric-heart-rate', unit: 'bpm' },
  spo2: { labelKey: 'visits-vital-oxygen-saturation', unit: '%' },
  temperature: { labelKey: 'doctor-metric-temperature', unit: '°C' },
  glucose: { labelKey: 'doctor-metric-glucose', unit: 'mg/dL' },
  fvc: { labelKey: 'visits-vital-fvc', unit: 'L' },
  fev1: { labelKey: 'visits-vital-fev1', unit: 'L' },
};

const INSTRUCTION_STATUS_COLOR: Record<DoctorInstruction['status'], string> = {
  PROCESSING: 'orange',
  HANDLED: 'green',
  NOT_HANDLED: 'red',
};

const personName = (person?: { firstName?: string | null; lastName?: string | null } | null) =>
  person ? `${person.lastName ?? ''} ${person.firstName ?? ''}`.trim() : '';

const patientName = (patient: DoctorPatient) => `${patient.lastName ?? ''} ${patient.firstName ?? ''}`.trim();

const MovementArrow = ({ moved }: { moved: MetricAssessment['moved'] }) => {
  if (!moved) return null;
  return (
    <span aria-hidden className={`text-[12px] ${moved === 'up' ? 'text-amber-600' : 'text-sky-600'}`}>
      {moved === 'up' ? '↑' : '↓'}
    </span>
  );
};

/**
 * One metric on a patient's row. The `usual` line appears only when the value
 * moved: a baseline shown beside an unremarkable value is noise, and a baseline
 * shown when there is none would be a claim we cannot make.
 */
const MetricCell = ({
  labelKey,
  unit,
  value,
  moved,
  baseline,
}: {
  labelKey: string;
  unit: string;
  value: string;
  moved: MetricAssessment['moved'];
  baseline: string | null;
}) => {
  const { t } = useTranslation();
  return (
    <div className="min-w-[104px]">
      <div className="text-[11px] text-slate-400 truncate">{t(labelKey)}</div>
      <div className="flex items-baseline gap-1">
        <span className="text-[14px] font-medium text-slate-800 tabular-nums">{value}</span>
        <span className="text-[11px] text-slate-400">{unit}</span>
        <MovementArrow moved={moved} />
      </div>
      {moved && baseline && (
        <div className="text-[11px] text-slate-400 tabular-nums">
          {t('doctor-dashboard-measurements-usual', { value: baseline })}
        </div>
      )}
    </div>
  );
};

/** A patient's row in the measurements panel. Opens the visit the newest reading came from. */
const MeasurementRow = ({
  row,
  name,
  onOpen,
}: {
  row: PatientMeasurements;
  name: string;
  onOpen: (visitId: number) => void;
}) => {
  const byMetric = new Map(row.metrics.map((metric) => [metric.metric, metric]));
  const systolic = byMetric.get('systolic');
  const diastolic = byMetric.get('diastolic');

  // The pair reads as moved when EITHER component moved, and takes that
  // component's direction — a systolic jump is the finding whether or not the
  // diastolic followed it.
  const pairMoved = systolic?.moved ?? diastolic?.moved ?? null;

  // Shown whenever either side has a baseline, with the other side blank. The
  // two components are assessed independently and one can have the three
  // readings a median needs while the other does not; requiring both would
  // silently drop a real finding.
  const pairBaseline =
    systolic?.baseline != null || diastolic?.baseline != null
      ? `${systolic?.baseline == null ? EMPTY : Math.round(systolic.baseline)}/${
          diastolic?.baseline == null ? EMPTY : Math.round(diastolic.baseline)
        }`
      : null;

  const others = (['heartRate', 'spo2', 'temperature', 'glucose', 'fvc', 'fev1'] as const)
    .map((key) => ({ key, assessment: byMetric.get(key) }))
    .filter((entry): entry is { key: typeof entry.key; assessment: MetricAssessment } => Boolean(entry.assessment));

  // A row always carries at least one metric — that is what makes it a row — but
  // reducing over an empty list would crash the whole page rather than drop one
  // line, so the empty case is handled instead of assumed away.
  const newest = row.metrics.reduce<MetricAssessment | undefined>(
    (found, metric) => (!found || dayjs(metric.at).isAfter(dayjs(found.at)) ? metric : found),
    undefined
  );
  if (!newest) return null;
  const newestVisitId = newest.visitId;

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`doctor-measurement-row-${row.clientId}`}
      onClick={() => onOpen(newestVisitId)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onOpen(newestVisitId);
      }}
      className="flex items-start gap-4 py-3 flex-wrap cursor-pointer hover:bg-slate-50 rounded-lg px-1"
    >
      <div className="w-44 shrink-0 min-w-0">
        <div className="text-[13px] font-medium text-slate-700 truncate">{name || EMPTY}</div>
        <div className="text-[11px] text-slate-400 tabular-nums">
          {inDisplayZone(row.latestAt).format(LIST_DATE_FORMAT)}
        </div>
      </div>

      <div className="flex flex-1 flex-wrap gap-x-6 gap-y-2 min-w-[180px]">
        {/* Either component is enough to show the pair — dropping the reading
            because its other half is missing would hide a recorded measurement. */}
        {(systolic || diastolic) && (
          <MetricCell
            labelKey={METRIC_DISPLAY.systolic.labelKey}
            unit={METRIC_DISPLAY.systolic.unit}
            value={`${systolic?.value ?? EMPTY}/${diastolic?.value ?? EMPTY}`}
            moved={pairMoved}
            baseline={pairBaseline}
          />
        )}
        {others.map(({ key, assessment }) => (
          <MetricCell
            key={key}
            labelKey={METRIC_DISPLAY[key].labelKey}
            unit={METRIC_DISPLAY[key].unit}
            value={String(assessment.value)}
            moved={assessment.moved}
            baseline={assessment.baseline == null ? null : String(Math.round(assessment.baseline * 10) / 10)}
          />
        ))}
      </div>
    </div>
  );
};

/** One visit line in "Coming up". The service, not the caregiver. */
const VisitLine = ({ visit, onOpen }: { visit: VisitPopulated; onOpen: (visitId: number) => void }) => {
  const services = (visit.services ?? [])
    .map((line) => line.service?.name)
    .filter(Boolean)
    .join(', ');
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`doctor-dashboard-visit-${visit.id}`}
      onClick={() => onOpen(visit.id)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onOpen(visit.id);
      }}
      className="flex items-center gap-3 py-2.5 flex-wrap cursor-pointer hover:bg-slate-50 rounded-lg px-1"
    >
      <div className="flex-1 min-w-[140px]">
        <div className="text-[13px] font-medium text-slate-700 truncate">{personName(visit.client) || EMPTY}</div>
        <div className="text-[11px] text-slate-400 truncate">{services || EMPTY}</div>
      </div>
      <div className="text-[12px] text-slate-500 tabular-nums shrink-0">
        {inDisplayZone(visit.startTime).format(`${LIST_DATE_FORMAT} ${LIST_TIME_FORMAT}`)}
      </div>
    </div>
  );
};

const DoctorDashboardPage = () => {
  const { t } = useTranslation();
  const [openVisitId, setOpenVisitId] = useState<number | null>(null);

  const [period, setPeriod] = useState<DashboardPeriod>(DEFAULT_DOCTOR_PERIOD);

  const periodOptions = useMemo(
    () => DASHBOARD_PERIOD_ORDER.map((value) => ({ value, label: t(DASHBOARD_PERIOD_LABEL_KEYS[value]) })),
    [t]
  );

  // Fixed at mount so a re-render does not slide the windows and refetch.
  // Changing the period recomputes off this same instant.
  const reference = useMemo(() => new Date(), []);

  // Half-open [from, to). getDashboardPeriodWindow is the admin dashboard's and
  // is imported rather than reimplemented: it pins Monday explicitly instead of
  // trusting the active dayjs locale, which `startOf('week')` here did not.
  const periodWindow = useMemo(() => getDashboardPeriodWindow(period, reference), [period, reference]);

  const windows = useMemo(() => {
    const now = dayjs(reference);
    return {
      baselineFrom: now.subtract(BASELINE_DAYS, 'day').toISOString(),
      now: now.toISOString(),
    };
  }, [reference]);

  const patients = useQuery({
    queryKey: ['doctor', 'dashboard', 'patients'],
    queryFn: () => listMyPatients({ limit: 1000, offset: 0 }),
  });

  // Every visit of the period, whatever its status — the row counts what is on
  // the doctor's plate, and a visit still to happen is as much part of that as
  // one already carried out.
  const periodVisits = useQuery({
    queryKey: ['doctor', 'dashboard', 'period', periodWindow.from, periodWindow.to],
    queryFn: () =>
      listDoctorVisits({
        limit: PERIOD_FETCH_LIMIT,
        offset: 0,
        sort: 'start_time',
        'filter[start_time][gte]': periodWindow.from,
        // `lt`, not `lte`: `to` is the first instant OUTSIDE the window.
        'filter[start_time][lt]': periodWindow.to,
      }),
  });

  // A MEASUREMENT IS A MEASUREMENT whatever the visit's status. This asked for
  // COMPLETED visits only, which is not what a reading is: the number was taken
  // and written down, and whether anyone later marked the visit finished says
  // nothing about it. Cancelled is the one exclusion, and for a reason of its
  // own - that visit either never happened or was voided, so its readings must
  // not be averaged into anyone's usual value. The breaches query already draws
  // the line in exactly that place (see doctor_portal_sql.py).
  //
  // Bounded at `now` because this panel reports what HAS been recorded. A future
  // visit carrying vitals is a data artefact, and without the bound it would set
  // the patient's "latest reading" to a date that has not happened.
  const measurementVisits = useQuery({
    queryKey: ['doctor', 'dashboard', 'measurements', windows.baselineFrom],
    queryFn: () =>
      listDoctorVisits({
        limit: MEASUREMENT_FETCH_LIMIT,
        offset: 0,
        sort: '-start_time',
        'filter[status][ne]': 'CANCELLED',
        'filter[start_time][gte]': windows.baselineFrom,
        'filter[start_time][lte]': windows.now,
      }),
  });

  const upcoming = useQuery({
    queryKey: ['doctor', 'dashboard', 'upcoming', windows.now],
    queryFn: () =>
      listDoctorVisits({
        limit: UPCOMING_MAX,
        offset: 0,
        sort: 'start_time',
        // SCHEDULED only: a cancelled visit is still in the future and would
        // otherwise read as something the doctor is expected at.
        'filter[status][eq]': 'SCHEDULED',
        'filter[start_time][gte]': windows.now,
      }),
  });

  const myInstructions = useQuery({
    queryKey: ['doctor', 'dashboard', 'my-instructions'],
    // No visit id — every instruction of this doctor's, filtered to current below.
    queryFn: () => listDoctorInstructions({}),
  });

  // More rows exist than were fetched, so an unknown part of each patient's
  // history is missing. A median over the wrong half is worse than no median:
  // baselines are suppressed and the panel says why.
  const partial = (measurementVisits.data?.total ?? 0) > MEASUREMENT_FETCH_LIMIT;

  // The period gates what is LISTED. The 180-day fetch behind the baselines is
  // deliberately not touched by it: a median needs history, and at current
  // recording rates a 30-day baseline almost never reaches its three readings.
  const measurementRows = useMemo(() => {
    const rows = buildMeasurementRows(measurementVisits.data?.data ?? [], periodWindow.from);
    if (!partial) return rows;
    return rows.map((row) => ({
      ...row,
      metrics: row.metrics.map((metric) => ({ ...metric, baseline: null, moved: null })),
    }));
  }, [measurementVisits.data, periodWindow.from, partial]);

  // Memoised because `?? []` is a fresh array every render, which would rebuild
  // the name map on each one.
  const patientRows = useMemo(() => patients.data?.data ?? [], [patients.data]);
  const nameByClientId = useMemo(
    () => new Map(patientRows.map((patient) => [patient.clientId, patientName(patient)])),
    [patientRows]
  );

  // A set difference, not a subtraction. A measurement row can belong to someone
  // no longer on the roster — a link that ended still leaves their past visits
  // readable — and subtracting the counts would then quietly understate
  // how many current patients have nothing.
  const withoutMeasurements = useMemo(() => {
    const measured = new Set(measurementRows.map((row) => row.clientId));
    return patientRows.filter((patient) => !measured.has(patient.clientId)).length;
  }, [patientRows, measurementRows]);

  // Three of the four tiles are counted off these rows, so a truncated response
  // would silently under-report them. Only the visit count survives that, since
  // it reads `total`.
  const periodRows = periodVisits.data?.data ?? [];
  const periodTruncated = (periodVisits.data?.total ?? 0) > periodRows.length;
  const derived = (value: number) => (periodTruncated ? EMPTY : String(value));

  // No tile label names the period: it is named once, by the control and the
  // range beside it. Four labels repeating it only made sense while it was fixed.
  const periodKpis: Kpi[] = [
    {
      key: 'patients',
      labelKey: 'doctor-dashboard-kpi-patients',
      value: derived(new Set(periodRows.map((visit) => visit.client?.clientId).filter((id) => id != null)).size),
    },
    {
      key: 'visits',
      labelKey: 'doctor-dashboard-kpi-visits',
      value: String(periodVisits.data?.total ?? 0),
    },
    {
      key: 'completed',
      labelKey: 'doctor-dashboard-kpi-completed',
      value: derived(periodRows.filter((visit) => visit.status === 'COMPLETED').length),
    },
    {
      key: 'measurements',
      labelKey: 'doctor-dashboard-kpi-measurements',
      value: derived(periodRows.reduce((total, visit) => total + countReadings(visit), 0)),
    },
  ];

  const instructions = useMemo(() => {
    const decidedCutoff = dayjs().subtract(INSTRUCTION_DECIDED_DAYS, 'day');
    return (myInstructions.data?.data ?? [])
      .filter((instruction) => {
        if (instruction.status === 'PROCESSING') return true;
        // A decided instruction with no timestamp cannot be aged out; keep it rather
        // than hide a decision the doctor may never have seen.
        if (!instruction.decidedAt) return true;
        return inDisplayZone(instruction.decidedAt).isAfter(decidedCutoff);
      })
      .slice()
      .sort((a, b) => {
        // Undecided first — they are the ones still in the office's hands.
        if (a.status !== b.status) {
          if (a.status === 'PROCESSING') return -1;
          if (b.status === 'PROCESSING') return 1;
        }
        return inDisplayZone(b.createdAt).valueOf() - inDisplayZone(a.createdAt).valueOf();
      });
  }, [myInstructions.data]);

  const upcomingRows = upcoming.data?.data ?? [];

  if (patients.isError) {
    return <Alert type="error" showIcon message={t('doctor-patients-load-failed')} />;
  }

  return (
    // The layout's content area is `overflow-hidden` and every page owns its own
    // scrolling. The negative margins pull the scroller out over <main>'s padding
    // and the matching padding puts the content back, so the wheel works from the
    // gutters and the scrollbar sits at the edge of the pane.
    <div className="-mx-4 -my-2 min-h-0 flex-1 overflow-y-auto px-4 py-2">
      <div className="flex flex-col gap-4">
        {/* One control for the tiles and the measurements panel below them. It
            sits above exactly what it drives: proposals and coming up are not
            period-scoped and stay underneath. */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="text-[12px] text-slate-400" data-testid="doctor-period-range">
              {t('doctor-dashboard-period-range', {
                from: dayjs(periodWindow.from).format(LIST_DATE_FORMAT),
                to: dayjs(periodWindow.to).format(LIST_DATE_FORMAT),
              })}
            </div>
            <Segmented<DashboardPeriod> size="small" value={period} onChange={setPeriod} options={periodOptions} />
          </div>
          {periodVisits.isLoading ? (
            <Skeleton active paragraph={{ rows: 2 }} />
          ) : (
            <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
              {periodKpis.map((kpi) => (
                <KpiTile key={kpi.key} kpi={kpi} label={t(kpi.labelKey)} />
              ))}
            </div>
          )}
        </div>

        <Card
          title={
            <span className="inline-flex items-center">
              {t('doctor-dashboard-measurements-title')}
              {/* "Measurements" alone left a reader guessing between a latest
                  reading, a period average and a delta — three readings of the
                  same digits with three different conclusions. */}
              <FieldHint text={t('doctor-dashboard-measurements-help')} />
            </span>
          }
          extra={
            /* The same range as the control's, repeated so the card still states
               its own window once the control has scrolled out of view. */
            <span className="text-xs text-slate-400" data-testid="doctor-measurements-window">
              {t('doctor-dashboard-period-range', {
                from: dayjs(periodWindow.from).format(LIST_DATE_FORMAT),
                to: dayjs(periodWindow.to).format(LIST_DATE_FORMAT),
              })}
            </span>
          }
        >
          {measurementVisits.isLoading || patients.isLoading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : measurementVisits.isError ? (
            <Alert type="error" showIcon message={t('doctor-dashboard-measurements-load-failed')} />
          ) : patientRows.length === 0 ? (
            <Empty description={t('doctor-patients-empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <>
              {partial && (
                <Alert className="!mb-3" type="warning" showIcon message={t('doctor-dashboard-measurements-partial')} />
              )}
              {measurementRows.length === 0 ? (
                <Empty description={t('doctor-dashboard-measurements-empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
              ) : (
                <div className="flex flex-col divide-y divide-slate-100 max-h-[30rem] overflow-y-auto pr-1">
                  {measurementRows.map((row) => (
                    <MeasurementRow
                      key={row.clientId}
                      row={row}
                      name={nameByClientId.get(row.clientId) ?? ''}
                      onOpen={setOpenVisitId}
                    />
                  ))}
                </div>
              )}
              {/* Shown with rows on screen, not only when empty: with this data the
                  honest headline is almost always how few patients are covered. */}
              {withoutMeasurements > 0 && (
                <div className="text-[12px] text-slate-400 mt-3" data-testid="doctor-measurement-coverage">
                  {t('doctor-dashboard-measurements-coverage', {
                    without: withoutMeasurements,
                    total: patientRows.length,
                  })}
                </div>
              )}
            </>
          )}
        </Card>

        <div className="grid gap-4 lg:grid-cols-2 items-start">
          <Card title={t('doctor-dashboard-my-instructions-title')}>
            {myInstructions.isLoading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : instructions.length === 0 ? (
              <Empty description={t('doctor-dashboard-instructions-empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <div className="flex flex-col divide-y divide-slate-100 max-h-[20rem] overflow-y-auto pr-1">
                {instructions.map((instruction) => (
                  <div key={instruction.instructionId} className="flex items-center gap-2 py-2.5 flex-wrap">
                    <div className="flex-1 min-w-[120px]">
                      <div className="text-[13px] text-slate-700">{instruction.title}</div>
                      <div className="text-[11px] text-slate-400 truncate">
                        {personName({ firstName: instruction.clientFirstName, lastName: instruction.clientLastName }) ||
                          EMPTY}
                      </div>
                    </div>
                    <Tag className="!m-0 !text-[11px]">
                      {t(`doctor-instruction-priority-${instruction.priority.toLowerCase()}`)}
                    </Tag>
                    <Tag className="!m-0 !text-[11px]" color={INSTRUCTION_STATUS_COLOR[instruction.status]}>
                      {t(`doctor-instruction-status-${instruction.status.toLowerCase()}`)}
                    </Tag>
                    {instruction.decisionNote && (
                      <div className="w-full text-[12px] text-slate-500">
                        {t('doctor-dashboard-instructions-decided-note', { note: instruction.decisionNote })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>

          <Card title={t('doctor-dashboard-upcoming-title')}>
            {upcoming.isLoading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : upcomingRows.length === 0 ? (
              <Empty description={t('doctor-dashboard-upcoming-empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <div className="flex flex-col divide-y divide-slate-100">
                {upcomingRows.map((visit) => (
                  <VisitLine key={visit.id} visit={visit} onOpen={setOpenVisitId} />
                ))}
              </div>
            )}
          </Card>
        </div>

        <DoctorVisitModal visitId={openVisitId} onClose={() => setOpenVisitId(null)} />
      </div>
    </div>
  );
};

export default DoctorDashboardPage;
